import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Tooltip, Table, Icon, Badge, Card, Row, Col, Tag, Dropdown, Menu } from 'antd';
import Confirm from '../Confirm';

const tableStyles = `
@keyframes ct2-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
@keyframes ct2-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes ct2-bounce { 0%,100%{transform:translateY(0) scaleY(1)} 40%{transform:translateY(-5px) scaleY(1.05)} 70%{transform:translateY(2px) scaleY(0.96)} }
@keyframes ct2-shake  { 0%,100%{transform:rotate(0)} 20%{transform:rotate(-14deg)} 40%{transform:rotate(14deg)} 60%{transform:rotate(-8deg)} 80%{transform:rotate(8deg)} }
@keyframes ct2-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes ct2-spin   { 0%{transform:rotate(0)} 100%{transform:rotate(180deg)} }

.ct2-row-stripe td{background:#f7f9ff!important}
.ct2-badge-status{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700;white-space:nowrap}
.ct2-dot-status{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.ct2-status-pending{background:#fff7e6;border:1px solid #ffd591;color:#d46b08}
.ct2-status-pending .ct2-dot-status{background:#fa8c16;box-shadow:0 0 5px #fa8c16}
.ct2-status-approved{background:#f6ffed;border:1px solid #b7eb8f;color:#389e0d}
.ct2-status-approved .ct2-dot-status{background:#52c41a;box-shadow:0 0 5px #52c41a}
.ct2-status-rejected{background:#fff1f0;border:1px solid #ffa39e;color:#cf1322}
.ct2-status-rejected .ct2-dot-status{background:#ff4d4f;box-shadow:0 0 5px #ff4d4f}

.ct2-settings-btn{width:32px!important;height:32px!important;min-width:32px!important;padding:0!important;border-radius:8px!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-size:14px!important;border:1.5px solid #d6e8ff!important;background:#e6f7ff!important;color:#1890ff!important;transition:all .25s!important}
.ct2-settings-btn.open{background:#1890ff!important;color:#fff!important;border-color:#1890ff!important;box-shadow:0 3px 10px rgba(24,144,255,.35)!important}
.ct2-settings-btn.open .anticon-setting{animation:ct2-spin .4s cubic-bezier(.22,1,.36,1) forwards!important}
.ct2-settings-btn:hover{transform:translateY(-2px)!important;box-shadow:0 4px 12px rgba(24,144,255,.2)!important}

.ct2-action-menu.ant-dropdown-menu{border-radius:10px!important;border:1px solid #e6f0ff!important;box-shadow:0 8px 28px rgba(24,144,255,.12)!important;padding:5px!important;min-width:160px!important;animation:ct2-float 0.2s ease-out!important}
.ct2-action-menu .ant-dropdown-menu-item{border-radius:7px!important;padding:8px 12px!important;font-size:12px!important;font-weight:600!important;transition:all .2s!important;display:flex!important;align-items:center!important;gap:8px!important}
.ct2-action-menu .ant-dropdown-menu-item:hover{transform:translateX(3px)!important;background:#e6f7ff!important;color:#1890ff!important}
.ct2-action-menu .ant-dropdown-menu-item-disabled{opacity:0.5!important;cursor:not-allowed!important}
.ct2-action-menu .ant-dropdown-menu-item-disabled:hover{transform:none!important;background:transparent!important}
.ct2-badge-dot .ant-badge-dot{top:0!important;right:-2px!important;width:8px!important;height:8px!important;box-shadow:0 0 0 2px #fff!important}
`;

const StatusBadge = ({ status }) => {
  const map = {
    'PENDING':  { cls: 'ct2-status-pending',  label: 'PENDING',  icon: 'clock-circle' },
    'APPROVED': { cls: 'ct2-status-approved', label: 'APPROVED', icon: 'check-circle' },
    'REJECTED': { cls: 'ct2-status-rejected', label: 'REJECTED', icon: 'close-circle' },
  };
  const cfg = map[status] || { cls: 'ct2-status-pending', label: status, icon: 'info-circle' };
  return (
    <span className={`ct2-badge-status ${cfg.cls}`}>
      <span className="ct2-dot-status" />
      <Icon type={cfg.icon} style={{ fontSize: 12 }} />
      {cfg.label}
    </span>
  );
};

const ActionCell = ({ record, openModal, approveOrReject, setLoading }) => {
  const [open, setOpen] = useState(false);
  const isPending = record.status === 'PENDING';
  
  const handleApprove = () => {
    Confirm(approveOrReject, null, 'approved', record.id, setLoading);
  };
  
  const handleReject = () => {
    Confirm(approveOrReject, null, 'rejected', record.id, setLoading);
  };
  
  const handleViewDetails = () => {
    openModal(record, 'pr');
  };
  
  const handleAddRemarks = () => {
    openModal({ id: record.id, remarks: record.remarks }, 'remarks');
  };
  
  const menu = (
    <Menu className="ct2-action-menu">
      <Menu.Item key="view" onClick={handleViewDetails}>
        <Icon type="profile" style={{ fontSize: 14, color: '#1890ff' }} />
        View Details
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '4px 0' }} />
      
      <Menu.Item 
        key="approve" 
        onClick={handleApprove}
        disabled={!isPending}
        className={!isPending ? 'ant-dropdown-menu-item-disabled' : ''}
      >
        <Icon type="check-circle" style={{ fontSize: 14, color: isPending ? '#52c41a' : '#d9d9d9' }} />
        Approve Request
        {!isPending && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8c8c8c' }}>Not available</span>}
      </Menu.Item>
      
      <Menu.Item 
        key="reject" 
        onClick={handleReject}
        disabled={!isPending}
        className={!isPending ? 'ant-dropdown-menu-item-disabled' : ''}
      >
        <Icon type="close-circle" style={{ fontSize: 14, color: isPending ? '#ff4d4f' : '#d9d9d9' }} />
        Reject Request
        {!isPending && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8c8c8c' }}>Not available</span>}
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '4px 0' }} />
      
      <Menu.Item key="remarks" onClick={handleAddRemarks}>
        <Badge dot={record.remarks !== ''} className="ct2-badge-dot" style={{ marginRight: 8 }}>
          <Icon type="message" style={{ fontSize: 14, color: '#fa8c16' }} />
        </Badge>
        Add Remarks
        {record.remarks && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#52c41a' }}>Has remarks</span>}
      </Menu.Item>
    </Menu>
  );
  
  return (
    <Dropdown 
      overlay={menu} 
      trigger={['hover']}
      placement="bottomLeft"
      getPopupContainer={() => document.body} 
      onVisibleChange={v => setOpen(v)}
    >
      <Button 
        className={`ct2-settings-btn${open ? ' open' : ''}`} 
        size="small" 
        icon="setting"
      />
    </Dropdown>
  );
};

const PendingListTable = props => {
  const {
    prList,
    openModal,
    approveOrReject,
    setLoading,
    paginate,
    onPageChange,
    onSizeChange,
    recordLength,
  } = props;

  const stats = useMemo(() => {
    if (!prList || !prList.length) return { total: 0, pending: 0, approved: 0, rejected: 0 };
    return {
      total: prList.length,
      pending: prList.filter(d => d.status === 'PENDING').length,
      approved: prList.filter(d => d.status === 'APPROVED').length,
      rejected: prList.filter(d => d.status === 'REJECTED').length,
    };
  }, [prList]);

  const cardStyle = { 
    borderRadius: 10, 
    boxShadow: '0 2px 12px rgba(24,144,255,0.08)', 
    border: '1px solid #e6f0ff', 
    background: '#fff', 
    height: '100%' 
  };
  
  const statCards = [
    { title: 'Total', value: stats.total, icon: 'file-text', color: '#1890ff', bg: '#e6f7ff', gc: 'rgba(24,144,255,0.3)' },
    { title: 'Pending', value: stats.pending, icon: 'clock-circle', color: '#fa8c16', bg: '#fff7e6', gc: 'rgba(250,140,22,0.3)' },
    { title: 'Approved', value: stats.approved, icon: 'check-circle', color: '#52c41a', bg: '#f6ffed', gc: 'rgba(82,196,26,0.3)' },
    { title: 'Rejected', value: stats.rejected, icon: 'close-circle', color: '#ff4d4f', bg: '#fff1f0', gc: 'rgba(255,77,79,0.3)' },
  ];

  const columns = [
    {
      key: 'actions',
      align: 'center',
      width: 60,
      render: record => (
        <ActionCell 
          record={record} 
          openModal={openModal} 
          approveOrReject={approveOrReject} 
          setLoading={setLoading}
        />
      ),
    },
    {
      key: 'status',
      title: 'STATUS',
      align: 'center',
      width: 140,
      render: data => <StatusBadge status={data.status} />,
      filters: [
        { text: 'PENDING', value: 'PENDING' },
        { text: 'APPROVED', value: 'APPROVED' },
        { text: 'REJECTED', value: 'REJECTED' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      defaultSortOrder: 'descend',
      sortDirections: ['descend'],
    },
    {
      key: 'supplier',
      title: 'SUPPLIER',
      dataIndex: 'supplier',
      align: 'center',
      sorter: (a, b) => (a.supplier || '').localeCompare(b.supplier || ''),
    },
    {
      key: 'poNum',
      title: 'PURCHASE ORDER',
      dataIndex: 'poNum',
      align: 'center',
      render: val => val || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'joNum',
      title: 'JOB ORDER',
      dataIndex: 'joNum',
      align: 'center',
      render: val => val || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'prNum',
      title: 'PURCHASE REQUISITION',
      dataIndex: 'prNum',
      align: 'center',
      render: val => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {val}
        </Tag>
      ),
    },
    {
      key: 'date',
      title: 'DATE REQUESTED',
      align: 'center',
      width: 200,
      render: data => (
        <Tooltip title={moment(data.created_at).format('LLLL')}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontWeight: 600, color: '#1890ff' }}>
              {moment(data.created_at).format('YYYY-MM-DD')}
            </span>
            <span style={{ fontSize: 10, color: '#8c8c8c' }}>
              {moment(data.created_at).fromNow()}
            </span>
          </div>
        </Tooltip>
      ),
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <style>{tableStyles}</style>
      
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6}>
            <Card style={cardStyle} bodyStyle={{ padding: '18px 20px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ 
                  width: 46, 
                  height: 46, 
                  borderRadius: 12, 
                  background: card.bg, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexShrink: 0, 
                  '--gc': card.gc, 
                  animation: 'ct2-glow 2.4s ease-in-out infinite' 
                }}>
                  <Icon type={card.icon} style={{ fontSize: 20, color: card.color }} />
                </div>
                <div>
                  <div style={{ 
                    fontSize: 11, 
                    color: '#8c8c8c', 
                    fontWeight: 500, 
                    letterSpacing: '.3px', 
                    whiteSpace: 'nowrap' 
                  }}>
                    {card.title.toUpperCase()}
                  </div>
                  <div style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: card.color, 
                    lineHeight: 1.2 
                  }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Table Card */}
      <Card 
        bordered={false} 
        style={{ 
          borderRadius: 10, 
          boxShadow: '0 2px 12px rgba(24,144,255,0.07)', 
          border: '1px solid #e6f0ff' 
        }} 
        bodyStyle={{ padding: '0 0 8px 0' }}
      >
        <div style={{ 
          padding: '14px 20px 12px', 
          borderBottom: '1px solid #f0f5ff', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8 
        }}>
          <Icon type="shopping-cart" style={{ color: '#1890ff', fontSize: 16, animation: 'ct2-beat 1.8s ease-in-out infinite' }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>
            PURCHASE REQUESTS
          </span>
          <Badge 
            count={stats.total} 
            style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }} 
            overflowCount={9999} 
          />
          {stats.pending > 0 && (
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 4, 
              padding: '2px 8px', 
              borderRadius: 10, 
              marginLeft: 4, 
              background: '#fff7e6', 
              border: '1px solid #ffd591', 
              fontSize: 11, 
              fontWeight: 700, 
              color: '#d46b08' 
            }}>
              <Icon type="clock-circle" style={{ fontSize: 10 }} />
              {stats.pending} PENDING
            </span>
          )}
        </div>
        
        <Table
          bodyStyle={{ fontSize: 12, overflowX: 'auto' }}
          size="middle"
          columns={columns}
          dataSource={prList}
          rowKey="id"
          style={{ margin: 0 }}
          rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'ct2-row-stripe'}
          pagination={{
            total: recordLength,
            current: paginate.page,
            pageSize: paginate.pageSize,
            defaultPageSize: paginate.pageSize,
            showSizeChanger: true,
            onChange: onPageChange,
            onShowSizeChange: onSizeChange,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: 'both',
            style: { padding: '8px 16px', marginBottom: 0 }
          }}
          bordered
        />
      </Card>
    </div>
  );
};

PendingListTable.propTypes = {
  prList: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
  approveOrReject: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  recordLength: PropTypes.number.isRequired,
  onSizeChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  paginate: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default PendingListTable;